/**
 * Builds a short "brief of the work" from a homeowner request listing, shown to
 * vendors (e.g. on the inquiry/checkout page) so they can reference the job
 * details while writing their inquiry. Returns null for non-request listings.
 */

// Keys that are internal plumbing, shown elsewhere, or private — never in the brief.
const HIDDEN_BRIEF_KEYS = new Set([
  'listingType',
  'transactionProcessAlias',
  'unitType',
  'category',
  'categoryLevel1',
  'projectDescription',
  'location',
  'quoteCount',
  'vendorSelections',
  'contactName',
  'contactEmail',
  'contactPhone',
  'phone',
  'email',
]);

const humanize = v => {
  const s = String(v);
  // Leave already-spaced free text as-is; title-case slug-like values.
  return /\s/.test(s) ? s : s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

const formatBriefValue = v => {
  if (Array.isArray(v)) return v.map(humanize).join(', ');
  if (v && typeof v === 'object') return null;
  if (v == null || v === '') return null;
  return humanize(v);
};

/**
 * Whether a listing is a homeowner request (not a vendor's provider listing).
 *
 * @param {Object} listing
 * @returns {boolean}
 */
export const isRequestListing = listing =>
  listing?.attributes?.publicData?.listingType === 'request';

/**
 * Whether a request's description and photos are stored privately. Requests created
 * before request privacy keep a public description until the migration moves it, and
 * the "not public" copy must not appear on those.
 *
 * @param {Object} listing
 * @returns {boolean}
 */
export const hasPrivateDetails = listing =>
  isRequestListing(listing) && listing.attributes.publicData.detailsPrivate === true;

/**
 * The homeowner's description and photos are private: they come from
 * /api/request-details (see components/RequestPrivateDetails), never from the public
 * listing, whose description is only a generic summary.
 *
 * @param {Object} listing - denormalised listing entity
 * @param {Object} [privateDetails] - { description, photos } from /api/request-details
 * @returns {null | { location, details: Array<{label, value}>, description, photoCount }}
 */
export const buildWorkBrief = (listing, privateDetails) => {
  const publicData = listing?.attributes?.publicData || {};
  if (publicData.listingType !== 'request') {
    return null;
  }

  const details = Object.entries(publicData)
    .filter(([k]) => !HIDDEN_BRIEF_KEYS.has(k))
    .map(([k, v]) => ({ label: humanize(k), value: formatBriefValue(v) }))
    .filter(d => d.value);

  return {
    location: publicData.location?.address || null,
    details,
    description: privateDetails?.description || null,
    photoCount: Array.isArray(privateDetails?.photos) ? privateDetails.photos.length : 0,
  };
};
